import { StaffColor } from "../lib/staff-colors.ts";

interface StaffMember {
  id: string;
  name: string;
}

interface Props {
  staff: StaffMember[];
  activeId: string;
  staffColorMap: Map<string, StaffColor>;
  onChange: (id: string) => void;
  /** Optional: appointment count today, shown as a small dot. */
  todayCounts?: Map<string, number>;
}

/**
 * Horizontal scrollable strip of staff avatars for mobile. Tap to switch
 * which staff column is visible in the grid below. Active avatar is
 * filled (staff colour); inactive are outlined.
 */
export function MobileStaffStrip({ staff, activeId, staffColorMap, onChange, todayCounts }: Props) {
  if (staff.length <= 1) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        overflowX: "auto",
        padding: "10px 8px",
        marginBottom: 6,
        background: "var(--paper-raised)",
        borderRadius: 12,
        border: "1px solid var(--rule)",
        scrollbarWidth: "none",
      }}
    >
      {staff.map((s) => {
        const color = staffColorMap.get(s.id);
        const initial = s.name.charAt(0).toUpperCase();
        const active = s.id === activeId;
        const count = todayCounts?.get(s.id) ?? 0;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onChange(s.id)}
            aria-pressed={active}
            style={{
              flex: "0 0 auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              minWidth: 56,
              position: "relative",
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: active ? color?.badge ?? "var(--accent-light)" : "var(--paper-well)",
                color: active ? color?.badgeText ?? "var(--accent-text)" : "var(--ink-soft)",
                border: `2px solid ${active ? color?.border ?? "var(--accent)" : "var(--rule)"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                fontWeight: 600,
                position: "relative",
              }}
            >
              {initial}
              {count > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -2,
                    right: -2,
                    minWidth: 16,
                    height: 16,
                    padding: "0 4px",
                    borderRadius: 999,
                    background: "var(--ink)",
                    color: "var(--canvas)",
                    fontSize: 10,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {count}
                </span>
              )}
            </div>
            <span
              style={{
                fontSize: 11,
                color: active ? "var(--ink)" : "var(--ink-soft)",
                maxWidth: 64,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {s.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}
